// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useCallback, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { Box, LinearProgress } from '@mui/material';
import { InstrumentConfiguration } from '@daml.js/utility-registry-v0/lib/Utility/Registry/V0/Configuration/Instrument';
import FormDialog from '../../../components/Dialog/FormDialog';
import TextInput from '../../../components/Form/TextInput';
import useArchiveAndCreateInstrumentConfiguration from '../../../hooks/mutations/registry/configuration/useArchiveAndCreateInstrumentConfiguration';
import useRegistrarService from '../../../hooks/queries/registry/onboarding/useRegistrarService';
import {
  PartyCredentialRequirementElement,
  toPartyCredentialRequirements,
  fromPartyCredentialRequirements,
} from '../../../modules/Credential/Common';
import RequiredCredentialsInput from '../../../modules/Credential/RequiredCredentialsInput';
import {
  toInstrumentIdentifiers,
  fromInstrumentIdentifiers,
  InstrumentIdentifierElement,
} from '../../../modules/Instrument/Common';
import InstrumentIdentifiersInput from '../../../modules/Instrument/InstrumentIdentifiersInput';
import TemplateContract from '../../../utils/TemplateContract';
import { ERR_MOD_INST_CONF } from '../../../utils/strings';

type DialogProps = {
  open: boolean;
  onClose: () => void;
  disableDialog?: boolean;
  instrumentConfiguration: TemplateContract<InstrumentConfiguration>;
};

export const ModifyInstrumentConfigurationDialog = ({
  open,
  onClose,
  instrumentConfiguration,
  disableDialog,
}: DialogProps) => {
  const [instrumentId, setInstrumentId] = useState<string>(
    instrumentConfiguration.payload.defaultIdentifier.id,
  );
  const [additionalIdentifiers, setAdditionalIdentifiers] = useState<InstrumentIdentifierElement[]>(
    fromInstrumentIdentifiers(instrumentConfiguration.payload.additionalIdentifiers),
  );
  const [instrumentHolderCredentialRequirements, setInstrumentHolderCredentialRequirements] =
    useState<PartyCredentialRequirementElement[]>(
      fromPartyCredentialRequirements(instrumentConfiguration.payload.holderRequirements),
    );
  const [instrumentIssuerCredentialRequirements, setInstrumentIssuerCredentialRequirements] =
    useState<PartyCredentialRequirementElement[]>(
      fromPartyCredentialRequirements(instrumentConfiguration.payload.issuerRequirements),
    );

  const { showBoundary } = useErrorBoundary<Error>();
  const registrarService = useRegistrarService();
  const archiveAndCreateInstrumentConfiguration = useArchiveAndCreateInstrumentConfiguration();

  const resetState = useCallback(() => {
    setInstrumentId('');
    setAdditionalIdentifiers([]);
    setInstrumentHolderCredentialRequirements([]);
    setInstrumentIssuerCredentialRequirements([]);
  }, [
    setInstrumentId,
    setAdditionalIdentifiers,
    setInstrumentHolderCredentialRequirements,
    setInstrumentIssuerCredentialRequirements,
  ]);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);

  const handleSubmit = useCallback(async () => {
    if (!registrarService.data) {
      showBoundary(new Error(ERR_MOD_INST_CONF, { cause: 'Registrar service is not available.' }));
      return;
    }

    await archiveAndCreateInstrumentConfiguration({
      cid: registrarService.data.contractId,
      argument: {
        cid: instrumentConfiguration.contractId,
        payload: {
          instrumentId,
          additionalIdentifiers: toInstrumentIdentifiers(additionalIdentifiers),
          holderRequirements: toPartyCredentialRequirements(instrumentHolderCredentialRequirements),
          issuerRequirements: toPartyCredentialRequirements(instrumentIssuerCredentialRequirements),
        },
      },
    });
    handleClose();
  }, [
    registrarService.data,
    instrumentConfiguration.contractId,
    instrumentId,
    additionalIdentifiers,
    instrumentHolderCredentialRequirements,
    instrumentIssuerCredentialRequirements,
    archiveAndCreateInstrumentConfiguration,
    handleClose,
    showBoundary,
  ]);

  if (registrarService.isLoading) {
    return <LinearProgress />;
  }

  const disableSubmit =
    !instrumentId ||
    additionalIdentifiers.some(({ element: { source, id } }) => !source || !id) ||
    instrumentIssuerCredentialRequirements.some(
      ({ issuer, requiredClaims }) =>
        !issuer ||
        (issuer &&
          (!requiredClaims.length ||
            requiredClaims.some(({ property, value }) => !property || !value))),
    ) ||
    instrumentHolderCredentialRequirements.some(
      ({ issuer, requiredClaims }) =>
        !issuer ||
        (issuer &&
          (!requiredClaims.length ||
            requiredClaims.some(({ property, value }) => !property || !value))),
    );

  return (
    <FormDialog
      open={open}
      title="Modify Instrument Configuration"
      onCancel={handleClose}
      onSubmit={handleSubmit}
      submitName="Modify"
      disableDialog={disableDialog}
      isSubmitDisabled={disableSubmit}
    >
      <Box mb={3}>
        <TextInput
          label="Instrument Id"
          value={instrumentId}
          setValue={setInstrumentId}
          disabled
          required
        />
      </Box>
      <Box mb={3}>
        <InstrumentIdentifiersInput
          instrumentIdentifiers={additionalIdentifiers}
          setInstrumentIdentifiers={setAdditionalIdentifiers}
          title="Additional Instrument Identifiers"
        />
      </Box>
      <Box mb={3}>
        <RequiredCredentialsInput
          list={instrumentIssuerCredentialRequirements}
          setList={setInstrumentIssuerCredentialRequirements}
          title="Required Credentials for Mint Requestor"
        />
      </Box>
      <Box mb={3}>
        <RequiredCredentialsInput
          list={instrumentHolderCredentialRequirements}
          setList={setInstrumentHolderCredentialRequirements}
          title="Required Credentials for Holders"
        />
      </Box>
    </FormDialog>
  );
};
